import {useState} from 'react';
import {Link, useNavigate} from '@tanstack/react-router';
import {type GeneratorHandoff, stashGeneratorTournament} from '../start/generatorHandoff';

// Shared staged-reveal behaviour for the free generator landing pages. The generation itself is
// instant, but revealing a full tournament in the same frame as the click reads as "nothing
// happened" — so each page names its real steps and walks a short progress bar through them
// before showing the result.

const STAGE_MS = 550;
const FINISH_PAUSE_MS = 250;

export function useStagedGenerate(stages: string[]) {
    const [stage, setStage] = useState<number | null>(null);

    /** Walk the stages, then call `complete` (which sets the page's result state). */
    const run = (complete: () => void) => {
        if (stage !== null) return;
        setStage(0);
        stages.forEach((_, i) => setTimeout(() => setStage(i), i * STAGE_MS));
        setTimeout(() => {
            complete();
            setStage(null);
        }, stages.length * STAGE_MS + FINISH_PAUSE_MS);
    };

    return {stage, running: stage !== null, run, stages};
}

/** The filling bar + current step label, rendered under the generate button while running. */
export function GeneratorProgress({gen}: {gen: ReturnType<typeof useStagedGenerate>}) {
    if (gen.stage === null) return null;
    return (
        <div className="mt-4">
            <div className="h-2 w-full overflow-hidden rounded-full bg-[#EDEEF4]">
                <div className="h-full rounded-full bg-[#082F8A] transition-all duration-500"
                     style={{width: `${Math.round(((gen.stage + 1) / gen.stages.length) * 100)}%`}} />
            </div>
            <p className="mt-2 animate-pulse font-inter text-sm text-[#1a1a2e]/70">{gen.stages[gen.stage]}…</p>
        </div>
    );
}

/**
 * Compact conversion card shown inside the generator card once results exist — the counterpart of
 * the full MakeItLiveCta panel under the results. Pass `buildPayload` (same contract as
 * MakeItLiveCta) on pages with a generator handoff so the entered teams carry through signup;
 * pages without a handoff format link straight to signup instead.
 */
export function GeneratorTopCta({buildPayload, note = "Make it live with scores, standings and a shareable page."}: {
    buildPayload?: () => GeneratorHandoff | null;
    note?: string;
}) {
    const navigate = useNavigate();
    const go = () => {
        const payload = buildPayload?.();
        if (!payload || !payload.teams?.length) return;
        stashGeneratorTournament(payload);
        navigate({to: '/start/from-generator' as any});
    };
    return (
        <div className="mt-4 rounded-xl border-2 border-[#082F8A]/20 bg-[#F6F7FB] p-4">
            <p className="font-inter text-sm font-semibold text-[#1a1a2e]">Your draw is ready below 👇</p>
            <p className="font-inter text-xs text-[#1a1a2e]/60 mt-0.5">{note}</p>
            {buildPayload ? (
                <button onClick={go}
                        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#082F8A] py-2.5 font-inter font-semibold text-white transition hover:bg-[#06246b]">
                    Create my live tournament
                </button>
            ) : (
                <Link to={"/signup/player/" as any}
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#082F8A] py-2.5 font-inter font-semibold text-white transition hover:bg-[#06246b]">
                    Create my live tournament
                </Link>
            )}
        </div>
    );
}
